import { useState, useEffect } from "react";
import {
  Box,
  Text,
  Flex,
  Image,
  Button,
  useDisclosure,
  useToast,
  CloseButton,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "context/UserContext";

import { columns } from "./data";
import { SORT_NEWEST_FIRST, SORT_OLDEST_FIRST } from "utils/constants";
import Table from "components/table";
import Searchbar from "components/searchbar";
import CustomSelect from "components/select";
import MainLayout from "components/mainLayout";
import HeaderDropdown from "components/dropdown";
import SelectMethodModal from "./SelectMethodModal";
import useGetSendBags from "api/queries/useGetSendBags";
import ViewModal from "./ViewModal";
import { WarningIcon } from "@chakra-ui/icons";

const SendBags = () => {
  const navigate = useNavigate();
  const [sort, setSort] = useState("newest");
  const [search, setSearch] = useState("");
  const { user } = useUserContext();
  const [bags, setBags] = useState([]);
  const [viewBagData, setViewBagData] = useState([]);

  const {
    isOpen: isViewModalOpen,
    onOpen: onViewModalOpen,
    onClose: onViewModalClose,
  } = useDisclosure();

  const {
    isOpen: isSelectMethodModalOpen,
    onOpen: onSelectMethodModalOpen,
    onClose: onSelectMethodModalClose,
  } = useDisclosure();

  const toast = useToast();
  const bagsQuery = useGetSendBags({
    seller_id: user?.info?._id,
    sort: sort,
    search: search,
  });

  const addActionsToRows = (rows) => {
    const dataWithActions = rows?.map((row) => {
      const action = (
        <Flex alignItems={"center"}>
          <Image
            src={require("assets/svgs/eye.svg").default}
            onClick={() => {
              onViewModalOpen();
              setViewBagData(row);
            }}
            _hover={{ cursor: "pointer" }}
          />
        </Flex>
      );
      return {
        ...row,
        action: action,
      };
    });

    return dataWithActions;
  };

  useEffect(() => {
    if (bagsQuery?.data) {
      setBags(addActionsToRows(bagsQuery?.data));
      return;
    }
  }, [bagsQuery.data]);

  const handleSendBagClick = () => {
    const { address, city, postcode } = user?.info || {};
    if (!address || !city || !postcode) {
      const toastId = toast({
        duration: 3000,
        isClosable: true,
        status: "error",
        position: "bottom",
        render: ({ onClose }) => (
          <Flex
            align="center"
            bg="red.500"
            color="white"
            p={3}
            borderRadius="md"
            fontWeight={500}
          >
            <WarningIcon mr={2} />

            <Box flex="1">
              <Text fontSize="14px" fontFamily="PlusJakartaSansMedium">
                Please complete your address on{" "}
                <Text
                  as="span"
                  textDecoration="underline"
                  cursor="pointer"
                  fontWeight="bold"
                  onClick={() => {
                    navigate("/profile");
                    toast.close(toastId); 
                  }}
                >
                  Profile
                </Text>{" "}
                screen!
              </Text>
            </Box>
  
            <CloseButton onClick={onClose} color="white" />
          </Flex>
        ),
      });
      return; 
    }
    onSelectMethodModalOpen(); 
  };
  

  return (
    <MainLayout>
      <Flex minH={"100vh"} h={"100%"} direction={"column"}>
        <Box>
          <Flex
            bg="white"
            padding={8}
            align={"center"}
            justify={"space-between"}
          >
            <Flex alignItems={"center"}>
              <Text
                fontFamily={"PlusJakartaSansBold"}
                fontSize={"16px"}
                lineHeight={"16px"}
                textTransform={"uppercase"}
              >
                Send a bag
              </Text>
              <Image
                src={require("assets/svgs/info-circle.svg").default}
                pos={"relative"}
                bottom={"1px"}
                mx={2}
                _hover={{
                  cursor: "pointer",
                }}
              />
            </Flex>
            <Flex gap={2} justifyContent={"flex-end"}>
              <Button
                onClick={handleSendBagClick} // ✅ updated
                w={"140px"}
                py={6}
                bg={"#1C1C1C"}
                borderRadius={"12px"}
                color={"white"}
                textTransform={"capitalize"}
                alignSelf={"flex-end"}
                alignItems={"center"}
                justifyContent={"center"}
                fontSize={"12px"}
                fontFamily={"PlusJakartaSansBold"}
              >
                <Image
                  src={require("assets/svgs/truck.svg").default}
                  width={"16px"}
                  height={"16px"}
                  pos={"relative"}
                  mr={2}
                  _hover={{
                    cursor: "pointer",
                  }}
                />
                Send Bag
              </Button>
              <HeaderDropdown />
            </Flex>
          </Flex>
        </Box>

        <Flex bg={"#f8fbff"} padding={4} height={"100%"} gap={2}>
          <Flex flex={1} direction={"column"} padding={4}>
            <Flex justify={"space-between"} alignItems={"center"} mb={4}>
              <Searchbar onChange={setSearch} title={"Search"} />
              <Flex align={"center"} gap={2}>
                <CustomSelect
                  label={"Sort"}
                  onChange={setSort}
                  style={{ maxWidth: "200px" }}
                  inputStyle={{
                    border: "none",
                    background: "#fff",
                    color: "#898989",
                    fontSize: "14px",
                    fontFamily: "PlusJakartaSansMedium",
                  }}
                >
                  <option value={SORT_NEWEST_FIRST}>Newest First</option>
                  <option value={SORT_OLDEST_FIRST}>Oldest First</option>
                </CustomSelect>
              </Flex>
            </Flex>
            <Box minW={"100%"} w={"1px"} overflowX={"auto"}>
              <Table columns={columns} data={bags} />
            </Box>
          </Flex>
        </Flex>
      </Flex>

      <SelectMethodModal
        isOpen={isSelectMethodModalOpen}
        onClose={onSelectMethodModalClose}
        onSelect={(value) => {
          if (value === "dhl") {
            // navigate("/send-a-bag/dhl/address-details");
            window.location.href =
            "mailto:info@kidswearcollective.com?subject=Send%20Bag%20Request";          
          }
          if (value === "kwc") {
            navigate("/send-a-bag/drop-at-location");
          }
        }}
      />
      <ViewModal
        onClose={onViewModalClose}
        isOpen={isViewModalOpen}
        bag={viewBagData}
      />
    </MainLayout>
  );
};

export default SendBags;
